import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { isTestEnvironment, envLabels, envColors, getEnvironment } from "@/lib/environment";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { generateTestUser, generateProUser, createAndLoginUser } from "e2e/user-helpers";
import { useNotification } from "@/hooks/useNotification";
import { useUser } from "@/hooks/useUser";
import { useLogoutInvalidate } from "@/hooks/useLogoutInvalidate";
// import { addGoalsRoutines } from "instantdb/client/addGoalsRoutines";

export const EnvironmentIndicator = () => {
  const { isAuthenticated, refetchUser } = useUser();
  const isTest = isTestEnvironment();
  const environment = getEnvironment();
  const [isCreating, setIsCreating] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const { showNotification } = useNotification();
  const { logout } = useLogoutInvalidate();

  useEffect(() => {
    // only on dev and test hide my own visits
    if (!(import.meta.env.PROD && !isTest)) {
      console.info("hiding visits");
      localStorage.setItem("umami.disabled", "1");
    }
  }, [isTest]);

  const shouldShowEnvironmentIndicator = () => {
    const isTest = isTestEnvironment();
    return !(import.meta.env.PROD && !isTest);
  };

  // Only show in development and test environments
  if (!shouldShowEnvironmentIndicator()) return null;

  const createNewUser = async ({
    pro,
    onboardingCompleted,
  }: { pro?: boolean; onboardingCompleted?: boolean }) => {
    try {
      await logout();

      setIsCreating(true);
      const currentTestUser = pro
        ? generateProUser({ onboardingCompleted })
        : generateTestUser({ onboardingCompleted });

      await createAndLoginUser(currentTestUser);
      refetchUser();

      // Show success message with user credentials
      showNotification({
        message: "New user created and logged in",
        type: "success",
        description: `Email: ${currentTestUser.email}\nPassword: ${currentTestUser.password}`,
      });

      // Copy credentials to clipboard for easy access
      const credentials = `Email: ${currentTestUser.email}\nPassword: ${currentTestUser.password}`;
      navigator.clipboard.writeText(credentials).catch(console.error);
    } catch (error) {
      console.error("Error creating user:", error);
      showNotification({
        message: "Failed to create user",
        type: "error",
        description: error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const initializeGoalsAndRoutines = async () => {
    try {
      setIsInitializing(true);
      // await addGoalsRoutines(userId);
      showNotification({
        message: "Goals and routines added successfully",
        type: "success",
      });
    } catch (error) {
      console.error("Error initializing goals and routines:", error);
      showNotification({
        message: "Failed to add goals and routines",
        type: "error",
        description: error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
      setIsInitializing(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="environment-indicator"
          className={cn(
            "fixed bottom-4 right-4 z-[100] text-white px-3 py-1 rounded-full text-sm font-medium cursor-pointer pointer-events-auto",
            envColors[environment],
          )}
        >
          {envLabels[environment]}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuItem
          data-testid="create-new-user"
          onClick={() => createNewUser({})}
          disabled={isCreating}
          className={isCreating ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isCreating ? "Creating user..." : "Create user"}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="create-new-user-onboarding-completed"
          onClick={() => createNewUser({ onboardingCompleted: true })}
          disabled={isCreating}
          className={isCreating ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isCreating ? "Creating user..." : "Create user (onboarding completed)"}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="create-new-pro-user-onboarding-completed"
          onClick={() => createNewUser({ onboardingCompleted: true, pro: true })}
          disabled={isCreating}
          className={isCreating ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isCreating ? "Creating user..." : "Create PRO user (onboarding completed)"}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="create-new-pro-user"
          onClick={() => createNewUser({ pro: true })}
          disabled={isCreating}
          className={isCreating ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isCreating ? "Creating PRO user..." : "Create PRO user"}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="initialize-goals-and-routines"
          onClick={initializeGoalsAndRoutines}
          disabled={isInitializing || !isAuthenticated}
          className={isInitializing ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isInitializing ? "Adding goals and routines..." : "Add goals and routines"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
